<template>
  <div class="payments-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <div class="payments-container">
      <div class="page-header">
        <h1>Payments</h1>
        <p class="subtitle">View your payment history</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading payments...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="fetchTransactions">Try Again</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!transactions || transactions.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
          <line x1="1" y1="10" x2="23" y2="10"></line>
        </svg>
        <p>No payment transactions found.</p>
      </div>

      <!-- Transactions Table -->
      <div v-else class="transactions-table-wrapper">
        <table class="transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Gateway</th>
              <th>Transaction ID</th>
              <th>Subscription</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transaction in transactions" :key="transaction.paymentGatewayTransactionID">
              <td class="date-cell">
                {{ formatDate(transaction.paymentDate) }}
              </td>
              <td class="amount-cell">
                {{ formatCurrency(transaction.paymentAmount, transaction.currency) }}
              </td>
              <td class="status-cell">
                <span class="status-badge" :class="getStatusClass(transaction.paymentStatus)">
                  {{ formatStatus(transaction.paymentStatus) }}
                </span>
              </td>
              <td class="gateway-cell">
                {{ formatGateway(transaction.paymentGateway) }}
              </td>
              <td class="transaction-id-cell">
                {{ transaction.paymentGatewayTransactionID }}
              </td>
              <td class="subscription-cell">
                {{ transaction.subscriptionName || 'N/A' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '../utils/api'

export default {
  name: 'PaymentsView',
  data() {
    return {
      transactions: [],
      loading: true,
      error: null
    }
  },
  async created() {
    await this.fetchTransactions()
  },
  methods: {
    async fetchTransactions() {
      try {
        this.loading = true
        this.error = null
        
        const data = await api('/users/me/transactions', {}, this.$router)
        // Handle response wrapped in transactions property
        if (data && data.transactions && Array.isArray(data.transactions)) {
          this.transactions = data.transactions
        } else if (Array.isArray(data)) {
          this.transactions = data
        } else {
          this.transactions = []
        }
      } catch (error) {
        this.error = error.code || 'Failed to load payment transactions. Please try again.'
        console.error('Error fetching transactions:', error)
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatCurrency(amount, currency) {
      if (amount === null || amount === undefined) return 'N/A'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount)
    },
    formatStatus(status) {
      if (!status) return 'Unknown'
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ').toLowerCase()
    },
    formatGateway(gateway) {
      if (!gateway) return 'N/A'
      return gateway.charAt(0).toUpperCase() + gateway.slice(1).toLowerCase()
    },
    getStatusClass(status) {
      if (!status) return 'unknown'
      const normalized = status.toLowerCase()
      if (normalized === 'paid' || normalized === 'completed' || normalized === 'success') {
        return 'success'
      } else if (normalized === 'pending' || normalized === 'processing') {
        return 'pending'
      } else if (normalized === 'failed' || normalized === 'cancelled' || normalized === 'refunded') {
        return 'failed'
      }
      return 'unknown'
    }
  }
}
</script>

<style scoped>
.payments-page {
  position: relative;
  min-height: 100vh;
  padding: 40px 20px;
  overflow: hidden;
}

/* Background elements */
.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  pointer-events: none;
  animation: pulse-glow 8s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
}

.payments-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
  animation: fade-in 0.6s ease-out;
}

.page-header h1 {
  color: var(--text-primary);
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

/* Loading, Error, Empty states */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
  animation: fade-in 0.4s ease-out;
}

.loading-state p,
.error-state p,
.empty-state p {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

.error-state svg,
.empty-state svg {
  color: var(--text-muted);
}

.error-state svg {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.retry-btn {
  padding: 10px 24px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Transactions Table */
.transactions-table-wrapper {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  animation: slide-up 0.6s ease-out;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
}

.transactions-table thead {
  background: rgba(0, 0, 0, 0.2);
}

.transactions-table th {
  padding: 16px;
  text-align: left;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.transactions-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease;
}

.transactions-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

.transactions-table tbody tr:last-child {
  border-bottom: none;
}

.transactions-table td {
  padding: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

.date-cell {
  color: var(--text-primary);
  font-weight: 500;
}

.amount-cell {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 15px;
}

.status-cell {
  padding: 12px 16px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.success {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge.pending {
  background: rgba(251, 191, 36, 0.15);
  color: #f59e0b;
}

.status-badge.failed {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.status-badge.unknown {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-muted);
}

.gateway-cell {
  text-transform: capitalize;
}

.transaction-id-cell {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: var(--text-muted);
}

.subscription-cell {
  color: var(--text-secondary);
}

@keyframes slide-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1024px) {
  .transactions-table-wrapper {
    overflow-x: auto;
  }

  .transactions-table {
    min-width: 800px;
  }
}

@media (max-width: 767px) {
  .payments-page {
    padding: 24px 16px;
  }

  .page-header h1 {
    font-size: 28px;
  }

  .subtitle {
    font-size: 14px;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .transactions-table th {
    font-size: 12px;
  }
}
</style>
